import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface CategoryUsage {
  category_id: string;
  category_name: string;
  category_icon: string;
  total_conversations: number;
  unique_users: number;
  total_messages: number;
  avg_conversation_length: number;
  last_activity: string;
  user_engagement: {
    user_id: string;
    user_email: string;
    user_name: string;
    conversation_count: number;
    last_conversation_date: string;
  }[];
}

interface CategoryStats {
  total_categories: number;
  total_conversations: number;
  total_users: number;
  most_popular_category: string;
  least_popular_category: string;
  avg_conversations_per_user: number;
}

export const CategoryAnalytics: React.FC = () => {
  const [categoryUsage, setCategoryUsage] = useState<CategoryUsage[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryUsage | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  useEffect(() => {
    fetchCategoryAnalytics();
  }, []);

  const fetchCategoryAnalytics = async () => {
    try {
      setLoading(true);
      const response = await callAdmin('get-category-analytics', {});
      
      if (response.error) {
        setError(response.error);
      } else {
        setCategoryUsage((response.data as any)?.category_usage || []);
        setCategoryStats((response.data as any)?.stats || null);
      }
    } catch (err) {
      setError('Failed to fetch category analytics');
      console.error('Error fetching category analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEngagementLevel = (conversationCount: number) => {
    if (conversationCount >= 10) return { level: 'High', color: 'bg-green-100 text-green-800' };
    if (conversationCount >= 5) return { level: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
    return { level: 'Low', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Category Analytics</h2>
          <p className="text-muted-foreground">Track category usage and user engagement</p>
        </div>
        <Button onClick={fetchCategoryAnalytics} variant="outline" className="border-border text-foreground hover:bg-secondary">
          🔄 Refresh
        </Button>
      </div>

      {/* Overall Stats */}
      {categoryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 bg-card border-border">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{categoryStats.total_categories}</div>
              <div className="text-sm text-muted-foreground">Total Categories</div>
            </div>
          </Card>
          <Card className="p-4 bg-card border-border">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-400">{categoryStats.total_conversations}</div>
              <div className="text-sm text-muted-foreground">Total Conversations</div>
            </div>
          </Card>
          <Card className="p-4 bg-card border-border">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">{categoryStats.total_users}</div>
              <div className="text-sm text-muted-foreground">Active Users</div>
            </div>
          </Card>
          <Card className="p-4 bg-card border-border">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400">
                {categoryStats.avg_conversations_per_user.toFixed(1)}
              </div>
              <div className="text-sm text-muted-foreground">Avg Conversations/User</div>
            </div>
          </Card>
        </div>
      )}

      {/* Category Usage Table */}
      <Card className="overflow-hidden bg-card border-border">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-bold text-foreground">Category Usage Overview</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-slate-900 border-b border-border">
              <tr>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Conversations
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Unique Users
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Total Messages
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Avg Length
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-950/40 divide-y divide-border">
              {categoryUsage.map((category) => (
                <tr key={category.category_id} className="hover:bg-slate-900/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{category.category_icon}</span>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {category.category_name}
                        </div>
                        <div className="text-xs text-muted-foreground">ID: {category.category_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.total_conversations}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.unique_users}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.total_messages}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.avg_conversation_length.toFixed(1)} msgs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(category.last_activity)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowUserDetails(true);
                      }}
                    >
                      View Users
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* User Details Modal */}
      {showUserDetails && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Users in {selectedCategory.category_name} ({selectedCategory.category_icon})
              </h3>
              <button
                onClick={() => setShowUserDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedCategory.user_engagement.map((user) => {
                  const engagement = getEngagementLevel(user.conversation_count);
                  return (
                    <Card key={user.user_id} className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">
                            {user.user_name || 'No name'}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${engagement.color}`}>
                            {engagement.level}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">{user.user_email}</div>
                        <div className="text-sm text-gray-600">
                          <strong>{user.conversation_count}</strong> conversations
                        </div>
                        <div className="text-sm text-gray-500">
                          Last: {formatDate(user.last_conversation_date)}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              
              {selectedCategory.user_engagement.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No users have used this category yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


